/**
 * Словарь культур и севооборота — один на всё приложение.
 *
 * Он локальный и фиксированный намеренно: подпись и цвет культуры обязаны быть
 * одинаковыми на карте, в карточке контура и в карточке хозяйства, при любом
 * наборе данных в кадре. Собирать словарь из ответа значило бы получать разные
 * легенды на разных экранах одного и того же среза.
 *
 * Правило то же, что у эпистемики и ярусов: незнакомый код НЕ подменяется
 * похожим знакомым и не прячется — он показывается как неизвестный.
 */

import { defineMessages, type Message } from "../i18n/define";
import { tr } from "../i18n/runtime";

/** Порядок легенды и порядок цветов. Место кода в списке задаёт его оттенок. */
export const CROP_ORDER = [
  "COTTON",
  "GRAIN",
  "POTATO",
  "RICE",
  "FODDER",
  "MELON",
  "LEGUMES",
  "OTHER_CROPS",
] as const;

const CROP_LABEL: Record<string, Message> = defineMessages({
  COTTON: { id: "common.crop.COTTON", defaultMessage: "Хлопок", description: "Культура: хлопчатник (uz: gʻoʻza / paxta)" },
  // «Пшеница», а не «Зерновые» или справочное «Ғалла»: одно имя культуры на
  // всех экранах и в ответах ИИ (решение владельца 07.10.2026, В2).
  GRAIN: { id: "common.crop.GRAIN", defaultMessage: "Пшеница", description: "Культура: пшеница (озимая), uz: bugʻdoy" },
  POTATO: { id: "common.crop.POTATO", defaultMessage: "Картофель", description: "Культура" },
  RICE: { id: "common.crop.RICE", defaultMessage: "Рис", description: "Культура" },
  FODDER: { id: "common.crop.FODDER", defaultMessage: "Кормовые", description: "Группа культур: кормовые" },
  MELON: { id: "common.crop.MELON", defaultMessage: "Бахчевые", description: "Группа культур: бахчевые (дыни, арбузы)" },
  LEGUMES: { id: "common.crop.LEGUMES", defaultMessage: "Бобовые", description: "Группа культур: бобовые" },
  OTHER_CROPS: { id: "common.crop.OTHER_CROPS", defaultMessage: "Прочие культуры", description: "Группа культур: прочие" },
  // Коды справочника вне легенды (порядок цветов не меняется): без подписи
  // в реестре хозяйств печаталось «неизвестно: ORCHARD».
  VEGETABLE: { id: "common.crop.VEGETABLE", defaultMessage: "Овощи", description: "Группа культур: овощи" },
  ORCHARD: { id: "common.crop.ORCHARD", defaultMessage: "Сады", description: "Многолетние насаждения: сады" },
  FALLOW: { id: "common.crop.FALLOW", defaultMessage: "Пар", description: "Земля под паром (без культуры в сезон)" },
});

export interface CropLabel {
  label: string;
  known: boolean;
}

export const UNKNOWN = defineMessages({
  empty: { id: "common.vocab.unknown", defaultMessage: "неизвестно", description: "Код словаря не пришёл" },
  code: {
    id: "common.vocab.unknownCode",
    defaultMessage: "неизвестно: {code}",
    description: "Сервер прислал код словаря, которого интерфейс не знает; {code} — машинный код",
  },
});

/** Общий вход для словарей `Record<код, Message>`: незнакомый код называется незнакомым. */
export function lookup(dictionary: Record<string, Message>, code: string | null | undefined) {
  if (!code) return { label: tr(UNKNOWN.empty), known: false };
  const label = Object.prototype.hasOwnProperty.call(dictionary, code) ? dictionary[code] : undefined;
  return label
    ? { label: tr(label), known: true }
    : { label: tr(UNKNOWN.code, { code }), known: false };
}

export function cropLabel(code: string | null | undefined): CropLabel {
  return lookup(CROP_LABEL, code);
}

/** Коды прототипа (`cotton`, `wheat`) — к кодам канона. */
const CROP_ALIAS: Record<string, string> = { WHEAT: "GRAIN" };

/**
 * Имя культуры для экрана — ЕДИНСТВЕННЫЙ вход для подписи культуры.
 *
 * Справочник сервера называет культуры по-узбекски («Пахта», «Ғалла»), а
 * экраны — по-русски; раньше одни подписи брали имя справочника, другие —
 * этот словарь, и на одном экране стояли «Хлопок-2026» и «Пахта 2026».
 * Знакомый код всегда получает имя отсюда; имя сервера — только для кода,
 * которого словарь не знает (лучше чужое слово, чем «неизвестно: X»).
 */
export function cropName(code: string | null | undefined, serverName?: string | null): string {
  const upper = code ? code.toUpperCase() : code;
  const known = cropLabel(upper ? CROP_ALIAS[upper] ?? upper : upper);
  if (known.known) return known.label;
  return serverName ?? known.label;
}

/**
 * Роль культуры в двухлетнем цикле контура.
 *
 * Роль — свойство КУЛЬТУРЫ, а не отдельного посева: хлопок не бывает
 * второстепенным. Сервер отдаёт её вместе с занятостью, клиент только называет.
 */
const CROP_ROLE_LABEL: Record<string, Message> = defineMessages({
  main: { id: "common.cropRole.main", defaultMessage: "основная", description: "Роль культуры в севообороте: основная (хлопок)" },
  secondary: { id: "common.cropRole.secondary", defaultMessage: "второстепенная", description: "Роль культуры: второстепенная (после пшеницы)" },
});

export function cropRoleLabel(role: string | null | undefined): CropLabel {
  return lookup(CROP_ROLE_LABEL, role);
}

/**
 * Фаза двухлетнего севооборота контура.
 *
 * Цикл: хлопок (весна→осень) → озимые зерновые (осень→июнь) →
 * второстепенная культура (июль→осень) → зимний отдых → хлопок.
 * `rest` — это НЕ «данных нет», а заявленный отдых земли.
 */
const ROTATION_PHASE_LABEL: Record<string, Message> = defineMessages({
  cotton: { id: "common.rotation.cotton", defaultMessage: "Хлопок", description: "Фаза севооборота контура" },
  winter_grain: { id: "common.rotation.winter_grain", defaultMessage: "Озимые зерновые", description: "Фаза севооборота: озимая пшеница" },
  secondary: { id: "common.rotation.secondary", defaultMessage: "Второстепенная культура", description: "Фаза севооборота: повторный посев после пшеницы" },
  rest: { id: "common.rotation.rest", defaultMessage: "Отдых земли", description: "Фаза севооборота: земля отдыхает (не «нет данных»)" },
});

export function rotationPhaseLabel(phase: string | null | undefined): CropLabel {
  return lookup(ROTATION_PHASE_LABEL, phase);
}

/**
 * Стадия занятости относительно даты среза.
 *
 * «Завершена» означает состоявшуюся работу, а не истёкший плановый срок:
 * пока последняя работа кампании не закрыла цикл, занятость остаётся «идёт».
 */
const OCCUPANCY_STATUS_LABEL: Record<string, Message> = defineMessages({
  preliminary: { id: "common.occupancy.preliminary", defaultMessage: "не начата", description: "Занятость контура культурой: ещё не начата" },
  active: { id: "common.occupancy.active", defaultMessage: "идёт", description: "Занятость контура культурой: идёт" },
  completed: { id: "common.occupancy.completed", defaultMessage: "завершена", description: "Занятость контура культурой: завершена" },
});

export function occupancyStatusLabel(status: string | null | undefined): CropLabel {
  return lookup(OCCUPANCY_STATUS_LABEL, status);
}

/**
 * Фазовая группа хозяйства: половина земли и её место в цикле на этот год.
 *
 * Две метки, а не три: сдвиг двухлетний, и третья группа означала бы другой
 * цикл, а не другое имя.
 */
const ROTATION_GROUP_LABEL: Record<string, Message> = defineMessages({
  A: { id: "common.rotationGroup.A", defaultMessage: "фаза A — в этом году под хлопком", description: "Фазовая группа хозяйства в двухлетнем севообороте" },
  B: { id: "common.rotationGroup.B", defaultMessage: "фаза B — пшеница прошлого сева и второстепенные", description: "Фазовая группа хозяйства в двухлетнем севообороте" },
});

export function rotationGroupLabel(label: string | null | undefined): CropLabel {
  return lookup(ROTATION_GROUP_LABEL, label);
}
